import React, { useState, useEffect } from 'react';
import {
  AreaChart, Area, BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';
import {
  TrendingUp, DollarSign, Users, Star, Clock, Activity, BarChart3, Target,
  Award, Zap, MessageSquare, ShieldCheck, Utensils, Eye, Heart, Share2, ThumbsUp
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useScope } from '../contexts/ScopeContext';
import { API_ENDPOINTS } from '../config/apiRouting';
import {
  useOverviewData, useRevenueData, useReputationData,
  useSocialData, useTeamData,
  type RecoveryFunnelStep, type MenuPerformanceEntry
} from '../hooks/useCommandCenterData';
import StatCard from './command-center/StatCard';
import SectionHeader from './command-center/SectionHeader';
import ServerLeaderboard from './command-center/ServerLeaderboard';
import RevenueTimeline from './command-center/RevenueTimeline';
import RecoveryFunnel from './command-center/RecoveryFunnel';
import MenuPerformanceTable from './command-center/MenuPerformanceTable';
import AskBSO from './command-center/AskBSO';
import './CommandCenterDashboard.css';

type TabKey = 'command-center' | 'revenue' | 'reviews' | 'social' | 'team';

interface StoreOption {
  storeId: string;
  name: string;
}

const TABS: { key: TabKey; label: string; icon: React.ReactNode }[] = [
  { key: 'command-center', label: 'Command Center', icon: <Activity size={16} /> },
  { key: 'revenue', label: 'Revenue & Sales', icon: <DollarSign size={16} /> },
  { key: 'reviews', label: 'Reviews & Recovery', icon: <Star size={16} /> },
  { key: 'social', label: 'Social & Marketing', icon: <Eye size={16} /> },
  { key: 'team', label: 'Team Performance', icon: <Award size={16} /> },
];

const ACCENT_COLORS = ['#6366f1', '#10b981', '#06b6d4', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];
const CHART_GRADIENT_ID = 'cc-revenue-gradient';

const getPlatformLogo = (platform: string) => {
  switch ((platform || '').toLowerCase()) {
    case 'google':
      return (
        <svg viewBox="0 0 24 24" width="16" height="16">
          <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
          <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
          <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
          <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
        </svg>
      );
    case 'yelp':
      return (
        <svg viewBox="0 0 24 24" width="16" height="16">
          <path d="M20.16 12.594l-4.995 1.433c-.96.276-1.74-.8-1.176-1.63l2.768-4.076c.564-.83 1.865-.394 1.865.625v3.024c0 .244-.17.542-.462.624zm-7.166 5.1l.93-5.166c.17-.96 1.527-1.08 1.866-.17l1.66 4.47c.34.91-.627 1.78-1.386 1.25l-2.59-1.834c-.21-.148-.387-.34-.48-.55zm-2.498.04l-2.59 1.835c-.76.53-1.726-.34-1.387-1.25l1.66-4.47c.34-.91 1.697-.79 1.867.17l.93 5.166c.043.24-.033.488-.24.66-.053.04-.133.06-.24-.11zm-3.33-5.74l-4.994-1.434c-.96-.276-1.026-1.627-.096-2.002l4.56-1.84c.93-.375 1.81.63 1.247 1.46l-2.768 4.076c-.14.21-.37.374-.627.374-.128 0-.22-.035-.323-.103v-.53zM12.72 2.46l-1.62 9.04c-.17.96-1.527.96-1.697 0L7.78 2.46C7.61 1.5 8.57.634 9.4 1.14l2.42 1.592c.34.228.56.6.6 1.006l.3 1.72v-3z" fill="#D32323"/>
        </svg>
      );
    case 'facebook':
      return (
        <svg viewBox="0 0 24 24" width="16" height="16">
          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" fill="#1877F2"/>
        </svg>
      );
    default:
      return null;
  }
};

const CommandCenterDashboard: React.FC = () => {
  const { session } = useAuth();
  const { enterpriseId: scopeEnterpriseId } = useScope();
  const [activeTab, setActiveTab] = useState<TabKey>('command-center');
  const [storeId, setStoreId] = useState<string | null>(null);
  const [stores, setStores] = useState<StoreOption[]>([]);
  const [revenueTimeframe, setRevenueTimeframe] = useState('7d');
  const [showAskBSO, setShowAskBSO] = useState(false);
  const [suggestions, setSuggestions] = useState<any[]>([]);

  const overview = useOverviewData(storeId);
  const revenue = useRevenueData(storeId, revenueTimeframe);
  const reputation = useReputationData(storeId);
  const social = useSocialData(storeId);
  const team = useTeamData(storeId);

  // Fetch stores on mount
  useEffect(() => {
    const fetchStores = async () => {
      try {
        const token = session?.getIdToken()?.getJwtToken?.() ||
          sessionStorage.getItem('idToken') || localStorage.getItem('idToken');
        if (!token) return;

        if (!scopeEnterpriseId) return;
        const res = await fetch(API_ENDPOINTS.enterpriseStores(scopeEnterpriseId), {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          const raw = Array.isArray(data) ? data : (data.stores || data.Items || []);
          const storeList = raw.map((s: any) => ({
            storeId: s.storeId || s.id,
            name: s.name || s.storeName || 'Store'
          }));
          setStores(storeList);
          if (storeList.length > 0 && !storeId) {
            setStoreId(storeList[0].storeId);
          }
        } else {
          console.error('Failed to fetch stores:', res.status, res.statusText);
        }
      } catch (err) {
        console.error('Error fetching stores:', err);
      }
    };
    fetchStores();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, scopeEnterpriseId]);

  // Fetch suggestions
  useEffect(() => {
    if (!storeId) return;
    const fetchSuggestions = async () => {
      try {
        const token = session?.getIdToken()?.getJwtToken?.() ||
          sessionStorage.getItem('idToken') || localStorage.getItem('idToken');
        if (!token) return;
        const res = await fetch(API_ENDPOINTS.suggestions(storeId), {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setSuggestions(data.suggestions || []);
        }
      } catch (err) {
        console.error('Error fetching suggestions:', err);
      }
    };
    fetchSuggestions();
  }, [storeId, session]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      if (activeTab === 'command-center') overview.refetch();
      if (activeTab === 'revenue') revenue.refetch();
      if (activeTab === 'reviews') reputation.refetch();
      if (activeTab === 'social') social.refetch();
      if (activeTab === 'team') team.refetch();
    }, 30000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, storeId]);

  const formatCurrency = (val: number | undefined | null) => {
    if (val == null) return '$0';
    if (val >= 1000000) return `$${(val / 1000000).toFixed(1)}M`;
    if (val >= 1000) return `$${(val / 1000).toFixed(1)}K`;
    return `$${val.toFixed(0)}`;
  };

  const formatNumber = (val: number | undefined | null) => {
    if (val == null) return '0';
    if (val >= 1000) return `${(val / 1000).toFixed(1)}K`;
    return val.toFixed(0);
  };

  const safeArray = (v: any): any[] => (Array.isArray(v) ? v : []);

  const RECOVERY_COLORS = ['#6366f1', '#06b6d4', '#f59e0b', '#10b981'];

  const toRecoveryData = (funnel: RecoveryFunnelStep[] | undefined) => {
    if (!funnel || !Array.isArray(funnel) || funnel.length === 0) return { steps: [] };
    return {
      steps: funnel.map((s, i) => ({
        name: s.stage,
        value: s.count,
        fill: RECOVERY_COLORS[i % RECOVERY_COLORS.length]
      }))
    };
  };

  const toMenuItemData = (items: MenuPerformanceEntry[]) =>
    items.map(m => ({
      item: m.name,
      orders: m.unitsSold,
      revenue: m.revenue,
      margin: m.profitMargin > 0 ? m.profitMargin : undefined,
      avgPrice: m.revenue && m.unitsSold ? Math.round((m.revenue / m.unitsSold) * 100) / 100 : undefined,
      trend: (m.trend > 0 ? 'up' : m.trend < 0 ? 'down' : undefined) as 'up' | 'down' | undefined
    }));

  const renderCommandCenterTab = () => {
    const data = overview.data;
    const loading = overview.loading;

    return (
      <div className="cc-tab-content">
        {/* KPI Cards */}
        <div className="cc-grid cc-grid--4">
          <StatCard
            icon={<DollarSign size={20} />}
            label="Today's Revenue"
            value={data ? formatCurrency(data.todayRevenue) : '--'}
            change={data?.revenueTrend}
            loading={loading}
            accent
          />
          <StatCard
            icon={<Users size={20} />}
            label="Covers"
            value={data ? formatNumber(data.guestCount) : '--'}
            change={data?.ordersTrend}
            loading={loading}
          />
          <StatCard
            icon={<Target size={20} />}
            label="Avg Check"
            value={data ? formatCurrency(data.avgTicketSize) : '--'}
            change={data?.avgTicketTrend}
            loading={loading}
          />
          <StatCard
            icon={<Star size={20} />}
            label="Rating"
            value={reputation.data?.avgRating?.toFixed(1) || '--'}
            suffix="/5"
            loading={reputation.loading}
          />
        </div>

        <div className="cc-split">
          {/* Revenue Chart */}
          <div className="cc-card cc-card--wide">
            <div className="cc-card__header">
              <SectionHeader icon={<TrendingUp size={18} />} title="Revenue Today" subtitle="Hourly breakdown" />
            </div>
            <div className="cc-card__body">
              <RevenueTimeline data={safeArray(data?.hourlyBreakdown)} loading={loading} />
            </div>
          </div>

          {/* Insights Sidebar */}
          <div className="cc-card cc-card--sidebar">
            <div className="cc-card__header">
              <SectionHeader icon={<Zap size={18} />} title="AI Insights" subtitle="Real-time suggestions" />
            </div>
            <div className="cc-card__body cc-insights">
              {suggestions.length === 0 && !loading && (
                <div className="cc-empty">No suggestions yet. Data will populate as orders flow in.</div>
              )}
              {suggestions.slice(0, 5).map((s: any, i: number) => (
                <div key={s.suggestionId || i} className={`cc-insight cc-insight--${s.priority || 'medium'}`}>
                  <div className="cc-insight__title">{s.title}</div>
                  <div className="cc-insight__desc">{s.description}</div>
                  {s.actionItems?.length > 0 && (
                    <ul className="cc-insight__actions">
                      {s.actionItems.slice(0, 2).map((a: string, j: number) => (
                        <li key={j}>{a}</li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Recent Reviews + Recovery Summary */}
        <div className="cc-grid cc-grid--2">
          <div className="cc-card">
            <div className="cc-card__header">
              <SectionHeader icon={<MessageSquare size={18} />} title="Recent Reviews" />
            </div>
            <div className="cc-card__body">
              {reputation.loading ? (
                <div className="cc-loading-text">Loading reviews...</div>
              ) : safeArray(reputation.data?.recentReviews).length === 0 ? (
                <div className="cc-empty">No recent reviews</div>
              ) : (
                <div className="cc-review-list">
                  {safeArray(reputation.data?.recentReviews).slice(0, 5).map((r: any, i: number) => (
                    <div key={i} className={`cc-review-card${(r.rating || 0) <= 2 ? ' cc-review-card--negative' : ''}`}>
                      <div className="cc-review-card__header">
                        <div className="cc-review-card__left">
                          {getPlatformLogo(r.platform)}
                          <span className={`cc-review-card__platform cc-review-card__platform--${(r.platform || 'google').toLowerCase()}`}>
                            {r.platform || 'Google'}
                          </span>
                        </div>
                        <div className="cc-stars">
                          {Array.from({ length: 5 }, (_, s) => (
                            <Star key={s} size={14} fill={s < (r.rating || 0) ? '#f59e0b' : 'none'} stroke={s < (r.rating || 0) ? '#f59e0b' : '#475569'} />
                          ))}
                        </div>
                      </div>
                      <div className="cc-review-card__text">{r.text || r.reviewText || ''}</div>
                      <div className="cc-review-card__time">{r.date || r.reviewDate || ''}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="cc-card">
            <div className="cc-card__header">
              <SectionHeader icon={<ShieldCheck size={18} />} title="Guest Recovery" />
            </div>
            <div className="cc-card__body">
              <RecoveryFunnel data={toRecoveryData(reputation.data?.recoveryFunnel)} loading={reputation.loading} />
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderRevenueTab = () => {
    const data = revenue.data;
    const loading = revenue.loading;

    return (
      <div className="cc-tab-content">
        {/* Timeframe Selector */}
        <div className="cc-timeframe-bar">
          {['1d', '7d', '30d'].map(tf => (
            <button
              key={tf}
              className={`cc-timeframe-btn ${revenueTimeframe === tf ? 'cc-timeframe-btn--active' : ''}`}
              onClick={() => setRevenueTimeframe(tf)}
            >
              {tf === '1d' ? 'Today' : tf === '7d' ? '7 Days' : '30 Days'}
            </button>
          ))}
        </div>

        {/* Revenue KPIs */}
        <div className="cc-grid cc-grid--4">
          <StatCard
            icon={<DollarSign size={20} />}
            label="Total Revenue"
            value={data ? formatCurrency(data.totalRevenue) : '--'}
            change={data?.weekOverWeekChange}
            loading={loading}
            accent
          />
          <StatCard
            icon={<Users size={20} />}
            label="Covers"
            value={data ? formatNumber(data.totalCovers || 0) : '--'}
            loading={loading}
          />
          <StatCard
            icon={<Target size={20} />}
            label="Avg Check"
            value={data?.avgCheck ? formatCurrency(data.avgCheck) : '--'}
            loading={loading}
          />
          <StatCard
            icon={<BarChart3 size={20} />}
            label="Orders"
            value={data ? formatNumber(data.totalOrders) : '--'}
            loading={loading}
          />
        </div>

        {/* Revenue Timeline */}
        <div className="cc-card">
          <div className="cc-card__header">
            <SectionHeader icon={<TrendingUp size={18} />} title="Revenue Timeline" subtitle={`${revenueTimeframe === '1d' ? 'Hourly' : 'Daily'} breakdown`} />
          </div>
          <div className="cc-card__body">
            <div className="cc-chart-container">
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={safeArray(data?.timeline)}>
                  <defs>
                    <linearGradient id={CHART_GRADIENT_ID} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#6366f1" stopOpacity={0.3} />
                      <stop offset="100%" stopColor="#6366f1" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1e293b44" />
                  <XAxis dataKey="date" stroke="#64748b" tick={{ fill: '#94a3b8', fontSize: 11 }} />
                  <YAxis stroke="#64748b" tick={{ fill: '#94a3b8', fontSize: 11 }} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}K`} />
                  <Tooltip
                    contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 8, color: '#e2e8f0' }}
                    formatter={(v: number) => [`$${v.toLocaleString()}`, 'Revenue']}
                  />
                  <Area type="monotone" dataKey="revenue" stroke="#6366f1" fill={`url(#${CHART_GRADIENT_ID})`} strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Comps, Voids, Discounts, Tips */}
        <div className="cc-grid cc-grid--4">
          <StatCard
            icon={<ShieldCheck size={20} />}
            label="Comps"
            value={data ? formatCurrency(data.comps || 0) : '--'}
            loading={loading}
          />
          <StatCard
            icon={<Activity size={20} />}
            label="Voids"
            value={data ? formatCurrency(data.voids || 0) : '--'}
            loading={loading}
          />
          <StatCard
            icon={<Target size={20} />}
            label="Discounts"
            value={data ? formatCurrency(data.discounts || 0) : '--'}
            loading={loading}
          />
          <StatCard
            icon={<TrendingUp size={20} />}
            label="Tips"
            value={data ? formatCurrency(data.totalTips || 0) : '--'}
            loading={loading}
          />
        </div>

        {/* Daypart Breakdown */}
        <div className="cc-card">
          <div className="cc-card__header">
            <SectionHeader icon={<Clock size={18} />} title="Daypart Breakdown" />
          </div>
          <div className="cc-card__body">
            <div className="cc-chart-container">
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={safeArray(data?.daypartBreakdown)}
                    cx="50%" cy="50%"
                    innerRadius={55} outerRadius={90}
                    dataKey="revenue"
                    nameKey="daypart"
                    paddingAngle={3}
                  >
                    {safeArray(data?.daypartBreakdown).map((_: any, i: number) => (
                      <Cell key={i} fill={ACCENT_COLORS[i % ACCENT_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 8, color: '#e2e8f0' }}
                    formatter={(v: number) => [`$${v.toLocaleString()}`, 'Revenue']}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="cc-legend">
              {safeArray(data?.daypartBreakdown).map((dp: any, i: number) => (
                <div key={i} className="cc-legend__item">
                  <span className="cc-legend__dot" style={{ background: ACCENT_COLORS[i % ACCENT_COLORS.length] }} />
                  <span className="cc-legend__label">{dp.daypart}</span>
                  <span className="cc-legend__value">{dp.percentOfTotal?.toFixed(0)}%</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderReviewsTab = () => {
    const data = reputation.data;
    const loading = reputation.loading;

    return (
      <div className="cc-tab-content">
        {/* Rating KPIs */}
        <div className="cc-grid cc-grid--4">
          <StatCard
            icon={<Star size={20} />}
            label="Average Rating"
            value={data?.avgRating?.toFixed(1) || '--'}
            suffix="/5"
            loading={loading}
            accent
          />
          <StatCard
            icon={<MessageSquare size={20} />}
            label="Total Reviews"
            value={data?.totalReviews || '--'}
            loading={loading}
          />
          <StatCard
            icon={<ShieldCheck size={20} />}
            label="Response Rate"
            value={data?.responseRate ? `${data.responseRate}` : '--'}
            suffix="%"
            loading={loading}
          />
          <StatCard
            icon={<Zap size={20} />}
            label="Recovered"
            value={safeArray(data?.recoveryFunnel).find((s: any) => s.stage === 'Recovered')?.count || '--'}
            loading={loading}
          />
        </div>

        {/* Rating Trend + Distribution */}
        <div className="cc-grid cc-grid--2">
          <div className="cc-card">
            <div className="cc-card__header">
              <SectionHeader icon={<TrendingUp size={18} />} title="Rating Trend" subtitle="Weekly averages" />
            </div>
            <div className="cc-card__body">
              <div className="cc-chart-container">
                <ResponsiveContainer width="100%" height={250}>
                  <LineChart data={safeArray(data?.ratingTrend)}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1e293b44" />
                    <XAxis dataKey="week" stroke="#64748b" tick={{ fill: '#94a3b8', fontSize: 11 }} />
                    <YAxis domain={[1, 5]} stroke="#64748b" tick={{ fill: '#94a3b8', fontSize: 11 }} />
                    <Tooltip contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 8, color: '#e2e8f0' }} />
                    <Line type="monotone" dataKey="google" stroke="#f59e0b" strokeWidth={2} dot={false} name="Google" />
                    <Line type="monotone" dataKey="yelp" stroke="#ef4444" strokeWidth={2} dot={false} name="Yelp" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          <div className="cc-card">
            <div className="cc-card__header">
              <SectionHeader icon={<BarChart3 size={18} />} title="Rating Distribution" />
            </div>
            <div className="cc-card__body">
              <div className="cc-chart-container">
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={safeArray(data?.distribution)}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1e293b44" />
                    <XAxis dataKey="stars" stroke="#64748b" tick={{ fill: '#94a3b8', fontSize: 11 }} />
                    <YAxis stroke="#64748b" tick={{ fill: '#94a3b8', fontSize: 11 }} />
                    <Tooltip contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 8, color: '#e2e8f0' }} />
                    <Bar dataKey="count" fill="#6366f1" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </div>

        {/* Recent Reviews + Recovery Funnel */}
        <div className="cc-grid cc-grid--2">
          <div className="cc-card">
            <div className="cc-card__header">
              <SectionHeader icon={<MessageSquare size={18} />} title="Recent Reviews" />
            </div>
            <div className="cc-card__body">
              <div className="cc-review-list">
                {safeArray(data?.recentReviews).slice(0, 8).map((r: any, i: number) => (
                  <div key={i} className={`cc-review-card${(r.rating || 0) <= 2 ? ' cc-review-card--negative' : ''}`}>
                    <div className="cc-review-card__header">
                      <div className="cc-review-card__left">
                        {getPlatformLogo(r.platform)}
                        <span className={`cc-review-card__platform cc-review-card__platform--${(r.platform || 'google').toLowerCase()}`}>
                          {r.platform || 'Google'}
                        </span>
                      </div>
                      <div className="cc-stars">
                        {Array.from({ length: 5 }, (_, s) => (
                          <Star key={s} size={14} fill={s < (r.rating || 0) ? '#f59e0b' : 'none'} stroke={s < (r.rating || 0) ? '#f59e0b' : '#475569'} />
                        ))}
                      </div>
                    </div>
                    <div className="cc-review-card__text">{r.text || r.reviewText || ''}</div>
                    <div className="cc-review-card__time">{r.date || r.reviewDate || ''}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="cc-card">
            <div className="cc-card__header">
              <SectionHeader icon={<ShieldCheck size={18} />} title="Recovery Pipeline" />
            </div>
            <div className="cc-card__body">
              <RecoveryFunnel data={toRecoveryData(data?.recoveryFunnel)} loading={loading} />
            </div>
          </div>
        </div>
      </div>
    );
  };

  const getPlatformIcon = (platform: string) => {
    switch (platform?.toLowerCase()) {
      case 'facebook': return <span className="cc-post-item__icon cc-post-item__icon--facebook"><i className="fab fa-facebook-f" /></span>;
      case 'instagram': return <span className="cc-post-item__icon cc-post-item__icon--instagram"><i className="fab fa-instagram" /></span>;
      case 'google': return <span className="cc-post-item__icon cc-post-item__icon--google"><i className="fab fa-google" /></span>;
      default: return <span className="cc-post-item__icon cc-post-item__icon--google"><Share2 size={16} /></span>;
    }
  };

  const formatPostDate = (dateStr: string) => {
    try {
      const d = new Date(dateStr);
      const now = new Date();
      const diffMs = now.getTime() - d.getTime();
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      if (diffDays === 0) return 'Today';
      if (diffDays === 1) return 'Yesterday';
      if (diffDays < 7) return `${diffDays}d ago`;
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    } catch { return ''; }
  };

  const renderSocialTab = () => {
    const data = social.data;
    const loading = social.loading;

    return (
      <div className="cc-tab-content">
        <div className="cc-grid cc-grid--4">
          <StatCard icon={<Eye size={20} />} label="Total Reach" value={data?.totalReach != null ? data.totalReach.toLocaleString() : '--'} loading={loading} accent />
          <StatCard icon={<Users size={20} />} label="Engagements" value={data?.totalEngagements != null ? data.totalEngagements.toLocaleString() : '--'} loading={loading} />
          <StatCard icon={<TrendingUp size={20} />} label="Engagement Rate" value={data?.engagementRate || '--'} suffix="%" loading={loading} />
          <StatCard icon={<MessageSquare size={20} />} label="Posts This Week" value={data?.postsThisWeek || '--'} loading={loading} />
        </div>

        <div className="cc-card">
          <div className="cc-card__header">
            <SectionHeader icon={<Eye size={18} />} title="Top Performing Posts" />
          </div>
          <div className="cc-card__body">
            {loading ? (
              <div className="cc-loading-text">Loading social data...</div>
            ) : safeArray(data?.topPosts).length === 0 ? (
              <div className="cc-empty">No social media data available. Connect your social accounts to see engagement metrics.</div>
            ) : (
              <div className="cc-post-list">
                {safeArray(data?.topPosts).map((post: any, i: number) => (
                  <div key={i} className="cc-post-item">
                    {getPlatformIcon(post.platform)}
                    <div className="cc-post-item__body">
                      <div className="cc-post-item__meta">
                        <span className="cc-post-item__platform">{post.platform}</span>
                        <span className="cc-post-item__date">{formatPostDate(post.postedAt)}</span>
                      </div>
                      <div className="cc-post-item__content">{post.content}</div>
                      <div className="cc-post-item__stats">
                        <span className="cc-post-item__stat"><ThumbsUp size={13} /> <span className="cc-post-item__stat-value">{post.likes}</span></span>
                        <span className="cc-post-item__stat"><MessageSquare size={13} /> <span className="cc-post-item__stat-value">{post.comments}</span></span>
                        <span className="cc-post-item__stat"><Share2 size={13} /> <span className="cc-post-item__stat-value">{post.shares}</span></span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderTeamTab = () => {
    const data = team.data;
    const loading = team.loading;

    return (
      <div className="cc-tab-content">
        {/* Team KPIs */}
        <div className="cc-grid cc-grid--4">
          <StatCard
            icon={<Award size={20} />}
            label="Top Server"
            value={data?.topServer?.serverName || '--'}
            loading={loading}
            accent
          />
          <StatCard
            icon={<Clock size={20} />}
            label="Avg Turn Time"
            value={data?.avgTurnTime ? `${data.avgTurnTime}` : '--'}
            suffix=" min"
            loading={loading}
          />
          <StatCard
            icon={<Users size={20} />}
            label="Active Servers"
            value={data?.serverLeaderboard?.length || '--'}
            loading={loading}
          />
          <StatCard
            icon={<DollarSign size={20} />}
            label="Avg Tips %"
            value={data?.avgTipPercent?.toFixed(1) || '--'}
            suffix="%"
            loading={loading}
          />
        </div>

        {/* Server Leaderboard */}
        <div className="cc-card">
          <div className="cc-card__header">
            <SectionHeader icon={<Award size={18} />} title="Server Leaderboard" subtitle="Ranked by revenue" />
          </div>
          <div className="cc-card__body">
            <ServerLeaderboard data={safeArray(data?.serverLeaderboard).map(s => ({
              name: s.name,
              covers: s.covers || s.orders,
              revenue: s.revenue,
              avgCheck: s.avgTicket,
              tips: s.tipTotal || 0,
              upsellRate: s.upsellRate,
              rating: s.rating
            }))} loading={loading} />
          </div>
        </div>

        {/* Menu Performance */}
        <div className="cc-card">
          <div className="cc-card__header">
            <SectionHeader icon={<Utensils size={18} />} title="Menu Performance" subtitle="Top selling items" />
          </div>
          <div className="cc-card__body">
            <MenuPerformanceTable data={toMenuItemData(safeArray(data?.menuPerformance))} loading={loading} />
          </div>
        </div>
      </div>
    );
  };

  const renderActiveTab = () => {
    switch (activeTab) {
      case 'command-center': return renderCommandCenterTab();
      case 'revenue': return renderRevenueTab();
      case 'reviews': return renderReviewsTab();
      case 'social': return renderSocialTab();
      case 'team': return renderTeamTab();
      default: return renderCommandCenterTab();
    }
  };

  return (
    <div className="cc-page">
      {/* Header */}
      <div className="cc-header">
        <div className="cc-header__brand">
          <div className="cc-header__logo">
            <Activity size={20} />
          </div>
          <div>
            <div className="cc-header__title">Command Center</div>
            <div className="cc-header__subtitle">Real-time restaurant intelligence</div>
          </div>
        </div>
        <div className="cc-header__controls">
          <select
            className="cc-store-select"
            value={storeId || ''}
            onChange={(e) => setStoreId(e.target.value || null)}
          >
            {stores.length === 0 && <option value="">Loading stores...</option>}
            {stores.map(s => (
              <option key={s.storeId} value={s.storeId}>{s.name}</option>
            ))}
          </select>
          <button
            className={`cc-ask-btn ${showAskBSO ? 'cc-ask-btn--active' : ''}`}
            onClick={() => setShowAskBSO(!showAskBSO)}
          >
            <MessageSquare size={16} />
            Ask BSO
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="cc-tabs">
        {TABS.map(tab => (
          <button
            key={tab.key}
            className={`cc-tab ${activeTab === tab.key ? 'cc-tab--active' : ''}`}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.icon}
            <span>{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="cc-content">
        {renderActiveTab()}
      </div>

      {/* Ask BSO Panel */}
      {showAskBSO && (
        <div className="cc-ask-panel">
          <AskBSO storeId={storeId || undefined} onClose={() => setShowAskBSO(false)} />
        </div>
      )}
    </div>
  );
};

export default CommandCenterDashboard;
